import { Icon, Link, List, mergeStyles } from '@fluentui/react';
import * as React from 'react';
import { capturePageAction } from '../../../../web/utils/oneDsUtil';
import { AlchemyInsightModel } from '../../../models/apiModels';
import { AutomationIds } from '../../../models/constants/automationIds';
import { IconNames, SourceApps } from '../../../models/constants/constants';
import { AlchemyInsightHTMLAllowedRegExp, sanitizeHTML } from '../../../utilities';
import { executeActions } from '../services/alchemyInsightActionService';
import { getCommonClassNames, getCommonStyles, getLinkStyles } from '../styles/commonStyles';
import { getContactUsClassNames, IContactUsStyles } from '../styles/contactUsStyles';
import { getAlchemyInsightsClassnames, getAlchemyInsightsStyles } from '../styles/selfHelpAlchemyInsightsStyles';

import { IStyledComponent } from '../styles/themesUI';
import { getString } from '../../../../web/utils/stringLoader';

interface SelfHelpAlchemyInsightsProps {
  insights: Array<AlchemyInsightModel>;
  isActionButtonEnabled: boolean;
  isMultiInsightsEnabled: boolean;
  openLinksInNewTab: boolean;
  showMoreInsightsExpanded: boolean;
  setExpandedInsight: (index: number, isExpanded: boolean) => any;
  sourceApp: SourceApps;
  isSignUp: boolean;
  toggleShowMoreInsights: () => void;
}

interface IRefState {
  updatedRef: boolean;
}

type ISelfHelpAlchemyInsightsProps = SelfHelpAlchemyInsightsProps & IStyledComponent<IContactUsStyles>;

export class SelfHelpAlchemyInsights extends React.Component<ISelfHelpAlchemyInsightsProps, IRefState> {
  private insightContentRef: any = React.createRef();
  private actionLock: boolean = false;
  private commonClassNames = getCommonClassNames(getCommonStyles, {});
  public constructor(props: ISelfHelpAlchemyInsightsProps) {
    super(props);
    this.state = {
      updatedRef: false,
    };
  }

  public componentDidMount() {
    this.replaceInsightsBTagWithButton();
  }

  public componentDidUpdate() {
    this.replaceInsightsBTagWithButton();
  }

  public render() {
    let insights;
    if (this.props.insights.length <= 0) {
      return (null);
    } else if (this.props.isMultiInsightsEnabled) {
      insights = this.props.insights;
    } else {
      insights = [this.props.insights[0]];
    }

    return (<List items={insights} onRenderCell={this.onRenderCell} />);
  }

  private handleRef = (ref: any) => {
    this.insightContentRef.current = ref;
    this.setState({ updatedRef: true });
  };

  private setExpandedInsight = (index: number, isExpanded: boolean) => {
    this.props.setExpandedInsight(index, isExpanded);
  };

  private handleToggleShowMoreInsights = () => {
    this.props.toggleShowMoreInsights();
  }

  private onRenderCell = (insight?: AlchemyInsightModel, index?: number) => {
    const updatedInsightHTML = this.updateInsightHTML(insight, index);
    const allowedAttrs = this.props.openLinksInNewTab ? ['target'] : undefined;
    const alchemyInsightsClassNames = getAlchemyInsightsClassnames(getAlchemyInsightsStyles, {
      theme: this.props.theme,
      isSignUp: this.props.isSignUp,
      showMoreInsightsExpanded: this.props.showMoreInsightsExpanded,
    });
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme });
    const linkStyles = getLinkStyles()
    const isExpanded = !!insight.IsExpanded;
    const showChevron = this.props.insights.length > 1 && this.props.isMultiInsightsEnabled;
    return (
      <div id="alchemyInsightSection" className={contactUsClassNames.panelBody}>
        <div className={alchemyInsightsClassNames.card}>
          {!!insight.Title && (
            <div className={alchemyInsightsClassNames.cardheaderSection}>
              <div className={alchemyInsightsClassNames.iconSection}>
                <div className={mergeStyles(alchemyInsightsClassNames.cardHeader, this.commonClassNames.inline)}>
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={sanitizeHTML(insight.Title)} />
                </div>
                {!!showChevron && (
                  <div
                    className={mergeStyles(alchemyInsightsClassNames.icon, this.commonClassNames.inline)}
                    aria-label={`Recommended solution ${index + 1} ${isExpanded ? 'expanded' : 'collapsed'}`}
                  >
                    <Icon
                      className={alchemyInsightsClassNames.expandCollapseIcon}
                      iconName={isExpanded ? IconNames.ChevronUp : IconNames.ChevronDown}
                      onClick={() => this.setExpandedInsight(index, isExpanded)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={alchemyInsightsClassNames.alchemyInsightSnipet} hidden={!isExpanded}>
            {/* eslint-disable-next-line react/no-danger */}
            <span data-automation-id={AutomationIds.InsightDescription} ref={this.handleRef} dangerouslySetInnerHTML={sanitizeHTML(updatedInsightHTML, allowedAttrs, AlchemyInsightHTMLAllowedRegExp)} />
          </div>
          {this.props.isSignUp && (
            <Link
              className={mergeStyles(alchemyInsightsClassNames.seeMoreSection, contactUsClassNames.link)}
              styles={linkStyles}
              onClick={this.handleToggleShowMoreInsights}
            >
              {this.props.showMoreInsightsExpanded
                ? getString('result_showLessText')
                : getString('result_showMoreText')}
            </Link>
          )}
        </div>
      </div>
    );
  };

  private replaceInsightsBTagWithButton = (): void => {
    if (!this.props.isActionButtonEnabled) {
      return;
    }

    let index;

    if (!this.props.isActionButtonEnabled || !this.insightContentRef || !this.insightContentRef.current) {
      return;
    }
    // Convert b tags inside insight, to action buttons
    const bElementList = this.insightContentRef.current.querySelectorAll('b[data-action-id]');
    for (index = 0; index < bElementList.length; index++) {
      const convertedString = bElementList[index].outerHTML.replace(
        /(?:<)(\w+)(\b.*)(?:<\/)\1(?:>)/,
        '<button$2</button>'
      );
      const convertedHtml = new DOMParser().parseFromString(convertedString, 'text/html');
      bElementList[index].parentNode.replaceChild(convertedHtml.body.firstElementChild, bElementList[index]);
    }

    // Add ocpActionLink class and onClick handler to insight action buttons
    const buttonElementList = this.insightContentRef.current.querySelectorAll('button[data-action-id]');
    for (index = 0; index < buttonElementList.length; index++) {
      buttonElementList[index].classList.add('ocpActionLink');
      buttonElementList[index].onclick = this.insightActionClickHandler;
    }
  };

  private insightActionClickHandler = async (event: any): Promise<void> => {
    if (this.actionLock) {
      return;
    }

    this.actionLock = true;
    const actions = event.target.getAttribute('data-action-id').split('|');

    try {
      const result: boolean = await executeActions(actions);
      this.actionLock = false;
      this.logActionExecution(actions, result);
    } catch (error) {
      this.actionLock = false;
      this.logActionExecution(actions, false);
    }
  };

  // Update Insight HTML to add logging attributes for clicks, css classes etc.
  private updateInsightHTML = (insight: AlchemyInsightModel, index: number): string => {
    if (!insight) {
      return '';
    }

    let html = insight.Description;

    try {
      const htmlDoc = new DOMParser().parseFromString(insight.Description, 'text/html');

      if (!!htmlDoc) {
        // In Portico articles, <h2> in section header makes it larger than the Insight header.
        // Replace <h2> with <p><b></b></p>.
        // Example queries:
        // 1. Start a Skype meeting
        // 2. About sync settings on Windows 10 devices
        // 3. Resize the Start menu
        const h2s = htmlDoc.querySelectorAll('h2');
        for (let i = 0; i < h2s.length; i++) {
          const b = document.createElement('b');
          h2s[i].childNodes.forEach(childNode => {
            h2s[i].removeChild(childNode);
            b.appendChild(childNode);
          });
          const p = document.createElement('p');
          p.appendChild(b);
          h2s[i].parentNode.replaceChild(p, h2s[i]);
        }

        // Insight links: add logging attributes
        const anchors = htmlDoc.querySelectorAll('a');
        if (!!anchors) {
          for (let i = 0; i < anchors.length; i++) {
            this.addLoggingAttributesForInsightClick(anchors[i], insight.BucketId, index);
          }
        }

        // Todo: As of 08/23, we've confirmed that Portico articles have been moved to SOC and can be removed
        // Action buttons in Portico articles: add logging attributes and css class
        // Input example: <button onclick="window.location.href = 'ms-settings:sync?activationSource=SMC-IA-4026102'">Open Sync settings</button>
        const porticoButtons = htmlDoc.querySelectorAll('button[onclick^="window.location.href"]');
        if (!!porticoButtons) {
          for (let i = 0; i < porticoButtons.length; i++) {
            this.addLoggingAttributesForInsightClick(porticoButtons[i], insight.BucketId, index);
            porticoButtons[i].classList.add('btn', 'btn-primary', 'sharp');
          }
        }

        // IE does not support firstElementChild, in that case use firstChild instead.
        const firstChild = !!htmlDoc.firstElementChild ? htmlDoc.firstElementChild : (htmlDoc.firstChild as Element);
        html = firstChild.innerHTML;
      }
    } catch (error) {
      console.error('Failed to update insight html.', error);
    }

    return html;
  };

  private addLoggingAttributesForInsightClick = (element: Element, bucketId: number, index: number): void => {
    element.setAttribute('data-bi-bhvr', 'SHOW');
    element.setAttribute('data-bi-area', 'selfHelpAlchemyInsight');
    element.setAttribute('data-bi-formnm', 'maxFunnel');
    element.setAttribute('data-bi-name', !!bucketId ? bucketId.toString() : '');
    element.setAttribute('data-bi-index', index.toString());
    element.setAttribute('data-bi-iscontactsuggestedselfhelp', 'true');
    if (this.props.openLinksInNewTab) {
      element.setAttribute('target', '_blank');
    }
  };

  private logActionExecution = (actionList: Array<string>, actionSuccessful: boolean) => {
    capturePageAction(null, {
      content: {
        contentName: this.props.insights[0].BucketId,
        actionButtonDetails: `${actionList.join(',')};${actionSuccessful}`,
        formnm: 'maxFunnel',
        isContactSuggestedSelfHelp: 'true',
        areaName: 'selfHelpAlchemyInsight',
      },
      behavior: 'SHOW',
    });
  };
}
